import { Box, Button } from "@mui/material";
import { BrandMappingFilters } from "~src/hooks/useBrandMappingV3";
import { ProductCategory } from "~src/types/productCategorisation";

type CategoryListProps = {
  categories: ProductCategory[];
  filters: BrandMappingFilters | undefined;
  onFiltersUpdated?: React.Dispatch<
    React.SetStateAction<BrandMappingFilters | undefined>
  >;
};

export const CategoryList: React.FC<CategoryListProps> = ({
                                                            categories,
                                                            filters,
                                                            onFiltersUpdated,
                                                          }) => {
  const renderedCategoryIds = new Set<number>();

  const formatText = (text: string) => {
    return text
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const toggleCategorySelection = (categoryId: number) => {
    if (onFiltersUpdated) {
      onFiltersUpdated((prevFilters: BrandMappingFilters | undefined) => {
        const updatedSelectedCategories =
          prevFilters?.selectedCategories?.includes(categoryId)
            ? prevFilters.selectedCategories.filter(
              (id: number) => id !== categoryId,
            )
            : [...(prevFilters?.selectedCategories || []), categoryId];

        if (updatedSelectedCategories.length === 0) {
          // Rename selectedCategories to _selectedCategories to mark it as unused.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { selectedCategories: _selectedCategories, ...remainingFilters } =
          prevFilters || {};
          return remainingFilters;
        }

        return {
          ...prevFilters,
          selectedCategories: updatedSelectedCategories,
        };
      });
    }
  };

  const renderCategories = (categories: ProductCategory[]) => {
    return categories.map((category) => {
      if (renderedCategoryIds.has(category.id)) {
        return null;
      }

      renderedCategoryIds.add(category.id);

      const isSelected = filters?.selectedCategories?.includes(category.id);

      return (
        <Box
          key={category.id}
          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
        >
          <Button
            variant={isSelected ? "contained" : "outlined"}
            onClick={() => toggleCategorySelection(category.id)}
            sx={{
              textTransform: "none",
              borderRadius: "16px",
              padding: "0 8px",
              minHeight: "32px",
            }}
          >
            {formatText(category.name)}
          </Button>
          {category.children && category.children.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {renderCategories(category.children)}
            </Box>
          )}
        </Box>
      );
    });
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {renderCategories(categories)}
    </Box>
  );
};
